import './ShareHint.scss';
import { ShareHintDetail } from './ShareHintDetail/ShareHintDetail';
import { ShareLinkDescription } from '../ShareLinkDescription/ShareLinkDescription';
import {
    FileShareType,
    ISharingLinkSettings,
    SharingAudience,
    ISharingInformation,
    ICustomLabeledAudienceConfig,
    ClientId
} from '../../interfaces/SharingInterfaces';
import * as React from 'react';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import Locale from '@ms/odsp-utilities/lib/locale/Locale';
import stringsResx from '../Share.resx';
import { isLoadingData } from '../../utilities/ShareHelper';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Coachmark } from 'office-ui-fabric-react/lib/Coachmark';
import { TeachingBubbleContent } from 'office-ui-fabric-react/lib/TeachingBubble';
import { createRef } from 'office-ui-fabric-react/lib/Utilities';
import { DirectionalHint } from 'office-ui-fabric-react/lib/common/DirectionalHint';
import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import { ABExperiment, ABExpHash } from '@ms/odsp-utilities/lib/logging/ABExperiment';

// feature flights
const CoachMarkInLinkSettingsExperiment = { ODB: 1176 };

export interface IShareHintState {
    isCoachmarkVisible: boolean;
}

export interface IShareHintProps {
    clientId: ClientId;
    companyName: string;
    currentSettings: ISharingLinkSettings;
    onShareHintClick?: () => void;
    sharingInformation: ISharingInformation;
    shouldTakeFocus?: boolean;
    linkRecipients: Array<IPerson>;
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
}

export class ShareHint extends React.Component<IShareHintProps, IShareHintState> {
    private _targetButton = createRef<HTMLElement>();
    private _isOnExperiment: boolean;
    private _odShareHintchevron = createRef<HTMLElement>();

    public constructor(props: IShareHintProps, context: any) {
        super(props);

        this._isOnExperiment = false;

        if (FeatureOverrides.isFeatureEnabled(CoachMarkInLinkSettingsExperiment)) {
            const experiment = new ABExperiment({
                name: "CoachMark",
                startDate: "08/2/2018",
                version: ABExpHash.v2
            });
            this._isOnExperiment = experiment.IsExperimentOn();
        }
        this.state = {
            isCoachmarkVisible: this._isOnExperiment
        };
    }

    public render(): React.ReactElement<{}> {
        const props = this.props;
        const permissionsType = this._getFileShareTypeFromAudience();
        const label = this._getLabelFromPermissionsType(permissionsType);
        const isClickable = !!this.props.onShareHintClick;
        let wrapperClasses = `od-ShareHint ${!isLoadingData(props.sharingInformation) ? 'od-ShareHint--shimmerFadeout od-ShareHint--loaded' : ''}`;
        let dataClasses = `od-ShareHint-data ${isClickable ? 'od-ShareHint--clickable' : ''}`;

        let markupShimmer;
        let markupData;

        markupShimmer = (
            <div className='od-ShareHint-shimmer'>
                <div className='od-ShareHint-iconHolder-shimmer'>
                    <svg viewBox="0 0 15 10" width="36" height="24">
                        <path
                            d="M0,0 L15,0 L15,10 L0,10 L0,0 Z M0,5 C0,7.76142375 2.23857625,10 5,10 C7.76142375,10 10,7.76142375 10,5 C10,2.23857625 7.76142375,2.22044605e-16 5,0 C2.23857625,-2.22044605e-16 0,2.23857625 0,5 L0,5 Z">
                        </path>
                    </svg>
                </div>
                <div className='od-ShareHint-text-shimmer'></div>
            </div>
        );
        const iconUrl = this.props.currentSettings.audience === SharingAudience.customLabeledSpecificPeople &&
            this.props.customLabeledAudienceConfig && this.props.customLabeledAudienceConfig.iconUrl;
        if (props.onShareHintClick) {
            markupData = (
                <button className={ dataClasses } ref={ this._targetButton }
                    onClick={ this._openLinkPermissions.bind(this) }
                    autoFocus={ props.shouldTakeFocus }>
                    <div className='od-ShareHint-iconHolder'>
                        <ShareLinkDescription
                            label={ label }
                            permissionsType={ permissionsType }
                            iconUrl={ iconUrl }
                            showLabel={ false }
                        />
                    </div>
                    <ShareHintDetail
                        clientId={ props.clientId }
                        companyName={ props.companyName }
                        currentSettings={ props.currentSettings }
                        sharingInformation={ props.sharingInformation }
                        linkRecipients={ props.linkRecipients }
                        customLabeledAudienceConfig={ props.customLabeledAudienceConfig }
                    />
                    { this._renderChevron(isClickable) }
                </button>
            );
        } else {
            markupData = (
                <div className={ dataClasses } { ...{ autoFocus: true } }>
                    <div className='od-ShareHint-iconHolder'>
                        <ShareLinkDescription
                            label={ label }
                            permissionsType={ permissionsType }
                            iconUrl={ iconUrl }
                            showLabel={ false }
                        />
                    </div>
                    <ShareHintDetail
                        clientId={ props.clientId }
                        companyName={ props.companyName }
                        currentSettings={ props.currentSettings }
                        sharingInformation={ props.sharingInformation }
                        linkRecipients={ props.linkRecipients }
                        customLabeledAudienceConfig={ props.customLabeledAudienceConfig }
                    />
                    { this._renderChevron(isClickable) }
                </div>
            );
        }
        return (
            <div className={ wrapperClasses }>
                { markupData }
                { markupShimmer }
                { this.state.isCoachmarkVisible &&
                    <Coachmark
                        target={ this._odShareHintchevron.current }
                        positioningContainerProps={ {
                            directionalHint: (Locale.isRightToLeft ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomRightEdge)
                        } }
                    >
                        <TeachingBubbleContent
                            calloutProps={ {
                                directionalHint: (Locale.isRightToLeft ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomRightEdge),
                                gapSpace: 0
                            } }
                            headline={ stringsResx.modifyPermissionsHeader }
                            hasCondensedHeadline={ true }
                            hasCloseIcon={ true }
                            closeButtonAriaLabel={ stringsResx.closeButtonLabel }
                            onDismiss={ this._onDismiss }
                        >
                            { stringsResx.linkSettingsExplanation1 }
                            <br />
                            { stringsResx.linkSettingsExplanation2 }
                        </TeachingBubbleContent>
                    </Coachmark>
                }
            </div>
        );
    }

    private _onDismiss = (): void => {
        this.setState({
            isCoachmarkVisible: false
        });
    }

    private _renderChevron(allowEdit: boolean) {
        if (allowEdit) {
            return (
                <div ref={ this._odShareHintchevron }  >
                    <Icon
                        iconName={ Locale.isRightToLeft ? 'ChevronLeft' : 'ChevronRight' }
                        className='od-ShareHint-chevron'
                    />
                </div>
            );
        }
    }

    private _openLinkPermissions(): void {
        const clickHandler = this.props.onShareHintClick;
        if (clickHandler) {
            clickHandler();
        }
    }

    private _getFileShareTypeFromAudience(): FileShareType {
        const audience = this.props.currentSettings.audience;

        if (audience === SharingAudience.anyone) {
            return FileShareType.anyone;
        } else if (audience === SharingAudience.organization) {
            return FileShareType.workGroup;
        } else if (audience === SharingAudience.existing) {
            return FileShareType.existing;
        } else if (audience === SharingAudience.customLabeledSpecificPeople) {
            return FileShareType.chat;
        } else { /* audience === SharingAudience.SPECIFIC_PEOPLE */
            return FileShareType.specificPeople;
        }
    }

    private _getLabelFromPermissionsType(shareType: FileShareType): string {
        const strings = stringsResx;

        if (shareType === FileShareType.anyone) {
            return strings.permissionsAnyoneString;
        } else if (shareType === FileShareType.workGroup) {
            return StringHelper.format(strings.permissionsCompanyString, this.props.companyName);
        } else if (shareType === FileShareType.existing) {
            return strings.permissionsExistingPeopleString;
        } else if (shareType === FileShareType.specificPeople) {
            return strings.permissionsSpecificPeopleString;
        }
    }
}